import { EDatasourceType, IActionDefinitionDto, IActionFolder, IDatasourceDto } from "@kuika/kuika-cl-model";
import { ITableFolder } from "@kuika/kuika-cl-model/src/models/table-folder";
import { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import { FolderHelper } from "../../../shared/utility/folder-helper";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { getKnownTables } from "./datasource-create-actions";
import { removeActionFromItsFolder } from "./datasource-row-actions";
import type { DatasourceRowMove } from "./datasources-landing-model";

/**
 * The work behind the Datasources landing's group and folder rows — the Sources tree's "+" entries
 * and the folder moves — as functions over the provider, with no UI attached. The provider calls
 * are the ones the Sources tree and AI Functions make; only the routing lives here.
 */

//  ────────────────────────────────────────────────────────────────────────────
//  Creating from a group's "+"
//  ────────────────────────────────────────────────────────────────────────────

/** What a create flow needs from the page that offers it. */
export interface IDatasourceTreeActionDeps {
  sd: IScreenDesignContextState;
  /** The plan/quota modal, for a domain with no room for another action. */
  showDomainAccessRequiredModal: (accessName: string) => void;
  /** Runs `open` now, or once the editor already open has given up its unsaved changes. */
  runGuarded: (open: () => void) => void;
}

/** The subscription asset every new action counts against. */
const ACTIONS_ACCESS_NAME: string = "Actions";

/** The actions the user wrote; the platform's system functions count towards nothing. */
const getUserActions = (sd: IScreenDesignContextState): IActionDefinitionDto[] =>
  (sd.actionDefinitions ?? []).filter(
    (action: IActionDefinitionDto): boolean =>
      action.actionType !== EDatasourceType.SystemJSFunctions && action.actionType !== EDatasourceType.SystemCSFunctions
  );

/** False, after asking for an upgrade, when the domain's plan has no room for another action. */
const hasRoomForAnotherAction = (deps: IDatasourceTreeActionDeps): boolean => {
  const hasAccess: boolean = KMainFunctions.isDomainHasAccess(ACTIONS_ACCESS_NAME, getUserActions(deps.sd));
  if (hasAccess === false) {
    deps.showDomainAccessRequiredModal(ACTIONS_ACCESS_NAME);
    return false;
  }
  return true;
};

/** The first free `Action<n>` — the name the Sources tree gives a new Select action. */
export const getNextActionName = (actions: IActionDefinitionDto[]): string => {
  let suffix: number = 1;
  while (actions.some((action: IActionDefinitionDto): boolean => action.name === `Action${suffix}`)) {
    suffix++;
  }
  return `Action${suffix}`;
};

/** "New {SQL|Blockly|SAP} Action": the datasource's action editor, on a new pre-named action. */
export const startNewAction = (deps: IDatasourceTreeActionDeps, datasource: IDatasourceDto): void => {
  if (!hasRoomForAnotherAction(deps)) return;
  const prefix: string = datasource.datasourceType === EDatasourceType.Blockly ? "Blockly_Action" : "Action";
  const name: string = KMainFunctions.defaultUniqueListElementNameGenerator(
    deps.sd.actionDefinitions ?? [],
    "name",
    prefix
  );
  deps.runGuarded((): void => deps.sd.setEditingActionsDatasource(datasource, undefined, true, false, undefined, name));
};

/** "New Select Action": the same editor, opened as the query builder's generic select. */
export const startNewSelectAction = (deps: IDatasourceTreeActionDeps, datasource: IDatasourceDto): void => {
  if (!hasRoomForAnotherAction(deps)) return;
  const name: string = getNextActionName(getUserActions(deps.sd));
  deps.runGuarded((): void =>
    deps.sd.setEditingActionsDatasource(datasource, undefined, true, false, undefined, name, true)
  );
};

/** A folder's "New … Action": the same editor on a new action filed in that folder. */
export const startNewActionInFolder = (
  deps: IDatasourceTreeActionDeps,
  datasource: IDatasourceDto,
  folderId: string
): void => {
  if (!hasRoomForAnotherAction(deps)) return;
  const name: string = getNextActionName(getUserActions(deps.sd));
  deps.runGuarded((): void => deps.sd.setEditingActionsDatasource(datasource, undefined, true, false, folderId, name));
};

/** "New Rest API Endpoint": the REST datasource's panel, on a new endpoint (index -1). */
export const startNewRestEndpoint = (deps: IDatasourceTreeActionDeps, datasource: IDatasourceDto): void => {
  deps.runGuarded((): void => deps.sd.setActiveEditingDatasource(datasource, -1));
};

/** Anything whose name a new table may not take by default. */
interface INamedEntity {
  name: string;
}

/**
 * The first free `Table<n>`: past the datasource's tables and the project's screens and processes
 * (all three become generated identifiers — the rules `validateNewTableName` holds a typed name to),
 * compared case-insensitively.
 *
 * A new table still being created holds its name too. It is in no list until it is saved, and "New
 * Table" pressed from its own editor would otherwise hand the same name back: the canvas keys a new
 * table's editor by that name, so the editor would stay mounted with the columns just given up.
 */
export const getNextTableName = (sd: IScreenDesignContextState, datasource: IDatasourceDto): string => {
  const taken: INamedEntity[] = [
    ...getKnownTables(sd, datasource),
    ...(sd.screenList ?? []),
    ...(sd.workFlowList ?? [])
  ];
  if (sd.activeCreatingTableName) taken.push({ name: sd.activeCreatingTableName });
  return KMainFunctions.defaultUniqueListElementNameGenerator(taken, "name", "Table");
};

/**
 * "New Table": the table editor, on a new pre-named table whose name is typed in the editor's own
 * bar — the way a new action opens. Named inside the guarded open, so a name is only ever taken for
 * an editor that actually opens.
 */
export const startNewTable = (deps: IDatasourceTreeActionDeps, datasource: IDatasourceDto): void => {
  deps.runGuarded((): void =>
    deps.sd.setEditingTablesDatasource(datasource, undefined, true, getNextTableName(deps.sd, datasource))
  );
};

//  ────────────────────────────────────────────────────────────────────────────
//  Folders
//  ────────────────────────────────────────────────────────────────────────────

/**
 * A new action folder: top level, or — with `parentFolder` — a sub-folder. Never a table folder: the
 * project does not save `databaseTablesFolders`, so one would vanish on the next reload with what it
 * held. An existing table folder can still be renamed or deleted below.
 */
export interface IDatasourceFolderCreate {
  mode: "create";
  folderKind: "action";
  datasourceId: string;
  /** The top-level folder the new one goes in. */
  parentFolder?: IActionFolder;
}

export interface IDatasourceActionFolderRename {
  mode: "rename";
  folderKind: "action";
  folder: IActionFolder;
}

export interface IDatasourceTableFolderRename {
  mode: "rename";
  folderKind: "table";
  folder: ITableFolder;
}

/** What the folder dialog was opened for. */
export type DatasourceFolderDialog =
  | IDatasourceFolderCreate
  | IDatasourceActionFolderRename
  | IDatasourceTableFolderRename;

export interface IDatasourceActionFolderRef {
  folderKind: "action";
  folder: IActionFolder;
}

export interface IDatasourceTableFolderRef {
  folderKind: "table";
  folder: ITableFolder;
}

/** A folder, with which of the project's two folder lists it lives in. */
export type DatasourceFolderRef = IDatasourceActionFolderRef | IDatasourceTableFolderRef;

/** The folders a new or renamed folder would sit beside. */
const getSiblingFolderNames = (
  dialog: DatasourceFolderDialog,
  actionFolders: IActionFolder[],
  tableFolders: ITableFolder[]
): string[] => {
  if (dialog.mode === "create") {
    if (dialog.parentFolder) {
      return (dialog.parentFolder.children ?? []).map((folder: IActionFolder): string => folder.name);
    }
    return actionFolders
      .filter(
        (folder: IActionFolder): boolean => folder.dataSourceType === dialog.datasourceId && !folder.parentFolderId
      )
      .map((folder: IActionFolder): string => folder.name);
  }

  if (dialog.folderKind === "table") {
    const renamed: ITableFolder = dialog.folder;
    return tableFolders
      .filter(
        (folder: ITableFolder): boolean => folder.dataSourceType === renamed.dataSourceType && folder.id !== renamed.id
      )
      .map((folder: ITableFolder): string => folder.name);
  }

  const renamed: IActionFolder = dialog.folder;
  const parent: IActionFolder | undefined = renamed.parentFolderId
    ? actionFolders.find((folder: IActionFolder): boolean => folder.id === renamed.parentFolderId)
    : undefined;
  const siblings: IActionFolder[] = parent
    ? (parent.children ?? [])
    : actionFolders.filter(
        (folder: IActionFolder): boolean => folder.dataSourceType === renamed.dataSourceType && !folder.parentFolderId
      );
  return siblings
    .filter((folder: IActionFolder): boolean => folder.id !== renamed.id)
    .map((folder: IActionFolder): string => folder.name);
};

/**
 * Whether the name is already taken beside the folder — the dialog's `checkFolderNameExists`. The
 * provider refuses a duplicate top-level action folder on its own, but says nothing for the other
 * cases, so the dialog asks first, the same way for all of them.
 */
export const datasourceFolderNameExists = (
  dialog: DatasourceFolderDialog,
  actionFolders: IActionFolder[],
  tableFolders: ITableFolder[],
  name: string
): boolean => getSiblingFolderNames(dialog, actionFolders ?? [], tableFolders ?? []).includes(name);

/**
 * Commits the folder dialog. A table folder is renamed through the table-folder list: the dialog
 * the Sources tree shares (`FolderCreateComponent`) only knows how to rename action folders.
 */
export const submitDatasourceFolderDialog = (
  sd: IScreenDesignContextState,
  dialog: DatasourceFolderDialog,
  name: string
): void => {
  if (dialog.mode === "create") {
    if (dialog.parentFolder) {
      sd.addActionFolderInFolder(dialog.parentFolder.id, name);
      return;
    }
    sd.addActionFolder(name, dialog.datasourceId);
    return;
  }

  if (dialog.folderKind === "table") {
    sd.editDatabaseTableFolder(dialog.folder.id, name);
    return;
  }
  if (dialog.folder.parentFolderId) {
    sd.editActionFolderInFolder(dialog.folder.parentFolderId, dialog.folder.id, name);
    return;
  }
  sd.editActionFolder(dialog.folder.id, name);
};

/** Deletes the folder; what it held goes back to being unfiled. */
export const deleteDatasourceFolder = (sd: IScreenDesignContextState, ref: DatasourceFolderRef): void => {
  if (ref.folderKind === "table") {
    sd.deleteDatabaseTableFolder(ref.folder.id);
    return;
  }
  if (ref.folder.parentFolderId) {
    sd.deleteActionFolderInFolder(ref.folder.parentFolderId, ref.folder.id);
    return;
  }
  sd.deleteActionFolder(ref.folder.id);
};

/**
 * The Sources tree's droppable for one schema's unfiled tables. Each schema panel needs its own:
 * rbd identifies a droppable by its id, so the one id every panel used to share told none of them
 * apart. A folder's droppable is the folder id, which never takes this prefix.
 */
const UNFILED_TABLES_DROPPABLE_PREFIX: string = "unfiled-tables:";

export const getUnfiledTablesDroppableId = (schemaName: string): string =>
  `${UNFILED_TABLES_DROPPABLE_PREFIX}${schemaName}`;

/** The folder a Sources-tree table droppable stands for; null for a schema's unfiled list. */
const getTableDroppableFolderId = (droppableId: string): string | null =>
  droppableId.startsWith(UNFILED_TABLES_DROPPABLE_PREFIX) ? null : droppableId;

/** A table drop on the Sources tree, as rbd reports it. The draggable id is the table's member id. */
export interface ISourcesTreeTableDrop {
  draggableId: string;
  sourceDroppableId: string;
  /** Absent when the table was let go clear of every list. */
  destinationDroppableId?: string;
}

/**
 * What a table drop on the Sources tree means, in the landing's terms so `applyDatasourceRowMove`
 * carries out both: into the folder it landed on, or — landed on a schema's list or clear of every
 * list — out of the folder it came from. Null when nothing would change.
 */
export const resolveSourcesTreeTableDrop = (drop: ISourcesTreeTableDrop): DatasourceRowMove | null => {
  const fromFolderId: string | null = getTableDroppableFolderId(drop.sourceDroppableId);
  const toFolderId: string | null = drop.destinationDroppableId
    ? getTableDroppableFolderId(drop.destinationDroppableId)
    : null;
  if (toFolderId === fromFolderId) return null;
  return { kind: "table", memberId: drop.draggableId, fromFolderId, toFolderId };
};

/**
 * Carries out a move `resolveDatasourceRowMove` accepted, as one provider write: both "add" paths
 * evict the item from every other folder first, so a folder-to-folder move needs no separate remove.
 */
export const applyDatasourceRowMove = (sd: IScreenDesignContextState, move: DatasourceRowMove): void => {
  if (move.kind === "table") {
    if (move.toFolderId) {
      sd.addDatabaseTableToFolder(move.toFolderId, move.memberId);
      return;
    }
    if (move.fromFolderId) sd.removeDatabaseTableFromFolder(move.fromFolderId, move.memberId);
    return;
  }

  if (!move.toFolderId) {
    removeActionFromItsFolder(sd, move.actionId);
    return;
  }
  // Top-level folders and sub-folders share one id space, so the lookup walks the children too.
  const target: IActionFolder | undefined = FolderHelper.getFolderById<IActionFolder>(
    move.toFolderId,
    sd.project?.actionFolders ?? []
  );
  if (!target) return;
  if (target.parentFolderId) {
    sd.addActionToFolderInFolder(target.parentFolderId, target.id, move.actionId);
    return;
  }
  sd.addActionToFolder(target.id, move.actionId);
};
